/* App.jsx — root: orquesta variantes, tema y estado de campaña (wired al backend) */

import React from "react";
import { TopBar } from "./sections/TopBar.jsx";
import { HeroNight, HeroDay } from "./sections/Hero.jsx";
import { SabiasQue } from "./sections/SabiasQue.jsx";
import { VideoSpotlight } from "./sections/VideoSpotlight.jsx";
import { ComoDonar } from "./sections/ComoDonar.jsx";
import { EnVivo } from "./sections/EnVivo.jsx";
import { ShareCampaign } from "./sections/ShareCampaign.jsx";
import { Galeria } from "./sections/Galeria.jsx";
import { Confianza } from "./sections/Confianza.jsx";
import { FAQ } from "./sections/FAQ.jsx";
import { LegalInfo } from "./sections/LegalInfo.jsx";
import { CTACierre } from "./sections/CTACierre.jsx";
import { Footer } from "./sections/Footer.jsx";
import { useCampaign } from "./shared.jsx";

// Configuración estática de presentación. El panel de tweaks del prototipo se
// eliminó para producción; los datos en vivo provienen del backend (no mock).
const TWEAK_DEFAULTS = {
  variant: "cinematica",
  theme: "auto",
  simulateLive: false,
};

const WHATSAPP_MESSAGE = "Quiero hacer una consulta sobre la Campaña #MecanicaParaQuilpue";
const WHATSAPP_URL = `https://wa.me/56975088232?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

function WhatsAppButton() {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Contactar por WhatsApp"
      className="floating-whatsapp"
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>
  );
}

function BackToTop() {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      type="button"
      aria-label="Volver arriba"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className={`floating-back-to-top ${show ? "is-visible" : "is-hidden"}`}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 19 V5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M5 12 L12 5 L19 12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </button>
  );
}

// undefined = fetch en curso, null = falló (cada sección hace fallback a su
// propio fetch), objeto = listo. Un solo request en vez de campaign +
// donations + faqs + gallery + site_settings por separado.
function useBootstrap() {
  const [bootstrap, setBootstrap] = React.useState(undefined);

  React.useEffect(() => {
    let cancelled = false;
    import("../lib/api.js")
      .then(({ fetchBootstrap }) => fetchBootstrap())
      .then((data) => { if (!cancelled) setBootstrap(data); })
      .catch(() => { if (!cancelled) setBootstrap(null); });
    return () => { cancelled = true; };
  }, []);

  return bootstrap;
}

export default function App() {
  const t = TWEAK_DEFAULTS;
  const bootstrap = useBootstrap();
  const campaign = useCampaign({ simulateLive: t.simulateLive, bootstrap });

  // theme: 'auto' usa night para cinemática, day para documental
  const effectiveTheme =
    t.theme === "auto" ? (t.variant === "cinematica" ? "night" : "day") : t.theme;

  function scrollTo(id) {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 72,
        behavior: "smooth",
      });
    }
  }

  return (
    <div className={`app theme-${effectiveTheme}`}>
      <TopBar onDonate={() => scrollTo("como")} />

      {t.variant === "cinematica" ? (
        <HeroNight campaign={campaign} onScrollTo={scrollTo} theme={effectiveTheme} />
      ) : (
        <HeroDay campaign={campaign} onScrollTo={scrollTo} theme={effectiveTheme} />
      )}

      <SabiasQue />
      <VideoSpotlight />
      <ComoDonar campaign={campaign} />
      <EnVivo campaign={campaign} />
      <ShareCampaign />
      <Galeria bootstrap={bootstrap} />
      <FAQ bootstrap={bootstrap} />
      <LegalInfo bootstrap={bootstrap} />
      <Confianza />
      <CTACierre onScrollTo={scrollTo} />
      <Footer />

      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}
